/**
 * Selection handles, width grips and hit testing (TD §11), in this order of priority:
 * width grips (only when nearer than the nearest dot), dots of the selection, icons, roads, rivers, and the
 * smallest area under the point.
 */
import { distPoly, nearestIdx, pip, polyArea, polyLen, SEG, type Derived } from '../../core/geom';
import type { NodeId, PlanDoc, Pt, VertId } from '../../shared/types';
import type { Selection } from '../store/editor';

/** A dot of the selection that can be dragged. */
export type Handle =
  | { kind: 'vert'; id: VertId; x: number; y: number; shared: boolean }
  | { kind: 'node'; id: NodeId; x: number; y: number; shared: boolean }
  | { kind: 'riverPt'; index: number; x: number; y: number; shared: false };

/** A width grip: a measuring line across a road at one dot (or across a river), with a grip on each edge. */
export interface Grip {
  /** The road dot it sets the width of, or null for a river (whole width). */
  nodeId: NodeId | null;
  mid: Pt;
  /** Unit normal across the road. */
  nx: number;
  ny: number;
  w: number;
  a: Pt;
  b: Pt;
}

/** What is under the pointer. */
export type Hit =
  | { type: 'grip'; grip: Grip }
  | { type: 'handle'; handle: Handle }
  | { type: 'object'; sel: Exclude<Selection, null> }
  | null;

/** Icon radius on screen in pixels (entrance, others). */
export const ICON_R_ENTRANCE = 17;
export const ICON_R = 11;

/** The dots of the selection. */
export function selectionHandles(doc: PlanDoc, sel: Selection): Handle[] {
  if (!sel) return [];
  const useCount = (ids: string[], kind: 'vert' | 'node') => {
    const n = new Map<string, number>();
    if (kind === 'vert') {
      for (const id of doc.plot?.verts ?? []) n.set(id, (n.get(id) ?? 0) + 1);
      for (const a of Object.values(doc.areas)) for (const v of new Set(a.verts)) n.set(v, (n.get(v) ?? 0) + 1);
    } else {
      for (const r of Object.values(doc.roads)) for (const v of new Set(r.nodes)) n.set(v, (n.get(v) ?? 0) + 1);
    }
    return (id: string) => (ids.includes(id) ? (n.get(id) ?? 0) > 1 : false);
  };
  if (sel.kind === 'area' || sel.kind === 'plot') {
    const ids = sel.kind === 'plot' ? doc.plot?.verts ?? [] : doc.areas[sel.id]?.verts ?? [];
    const shared = useCount(ids, 'vert');
    return [...new Set(ids)].flatMap((id) => { const v = doc.verts[id]; return v ? [{ kind: 'vert' as const, id, x: v.x, y: v.y, shared: shared(id) }] : []; });
  }
  if (sel.kind === 'road') {
    const ids = doc.roads[sel.id]?.nodes ?? [];
    const shared = useCount(ids, 'node');
    return [...new Set(ids)].flatMap((id) => { const n = doc.nodes[id]; return n ? [{ kind: 'node' as const, id, x: n.x, y: n.y, shared: shared(id) }] : []; });
  }
  if (sel.kind === 'river') {
    return (doc.rivers[sel.id]?.pts ?? []).map((p, index) => ({ kind: 'riverPt' as const, index, x: p[0], y: p[1], shared: false as const }));
  }
  return [];
}

function gripAt(P: readonly Pt[], si: number, w: number, nodeId: NodeId | null): Grip {
  const a = P[Math.max(0, si - 1)]!, b = P[Math.min(P.length - 1, si + 1)]!, m = P[si]!;
  const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1, nx = -(b[1] - a[1]) / l, ny = (b[0] - a[0]) / l;
  return { nodeId, mid: [m[0], m[1]], nx, ny, w, a: [m[0] + nx * w / 2, m[1] + ny * w / 2], b: [m[0] - nx * w / 2, m[1] - ny * w / 2] };
}

/** Width grips of the selected road (one per dot) or river (one, halfway). */
export function widthGrips(doc: PlanDoc, derived: Derived, sel: Selection): Grip[] {
  if (!sel) return [];
  if (sel.kind === 'road') {
    const r = doc.roads[sel.id], poly = derived.roadPolys[sel.id];
    if (!r || !poly || poly.pts.length < 2) return [];
    const seen = new Set<string>();
    return r.nodes.flatMap((nid, j) => {
      const n = doc.nodes[nid];
      if (!n || seen.has(nid)) return [];
      seen.add(nid);
      return [gripAt(poly.pts, Math.min(j * SEG, poly.pts.length - 1), n.w, nid)];
    });
  }
  if (sel.kind === 'river') {
    const rv = doc.rivers[sel.id], P = derived.riverPolys[sel.id];
    if (!rv || !P || P.length < 2) return [];
    const half = polyLen(P) / 2;
    let acc = 0, si = 0;
    for (let i = 0; i < P.length - 1 && acc < half; i++) { acc += Math.hypot(P[i + 1]![0] - P[i]![0], P[i + 1]![1] - P[i]![1]); si = i + 1; }
    return [gripAt(P, Math.min(si, P.length - 1), rv.w, null)];
  }
  return [];
}

/** Options for hitTest. */
export interface HitOptions {
  /** Metres per screen pixel. */
  pxm: number;
  selection: Selection;
  /** Show and hit width grips (Advanced). */
  grips: boolean;
  /** Hit the selection's dots. */
  handles: boolean;
}

/** The thing under map point (x, y); see the file comment for the order. */
export function hitTest(doc: PlanDoc, derived: Derived, x: number, y: number, o: HitOptions): Hit {
  const tol = 10 * o.pxm;
  const hs = o.handles ? selectionHandles(doc, o.selection) : [];
  let dotD = Infinity, dot: Handle | null = null;
  for (const h of hs) { const d = Math.hypot(h.x - x, h.y - y); if (d < dotD) { dotD = d; dot = h; } }
  if (o.grips && o.handles) {
    let gd = Infinity, grip: Grip | null = null;
    for (const g of widthGrips(doc, derived, o.selection)) for (const q of [g.a, g.b]) {
      const d = Math.hypot(q[0] - x, q[1] - y);
      if (d < gd) { gd = d; grip = g; }
    }
    if (grip && gd < tol * 0.9 && gd < dotD) return { type: 'grip', grip };
  }
  if (dot && dotD < tol) return { type: 'handle', handle: dot };
  return hitObject(doc, derived, x, y, o.pxm);
}

/** The object under (x, y), without handles: icons, roads, rivers, then the smallest area. */
export function hitObject(doc: PlanDoc, derived: Derived, x: number, y: number, pxm: number, only?: 'road'): Hit {
  const tol = 10 * pxm;
  if (!only) {
    const icons = Object.entries(doc.icons);
    for (let i = icons.length - 1; i >= 0; i--) {
      const [id, ic] = icons[i]!;
      if (Math.hypot(ic.x - x, ic.y - y) < (ic.type === 'entrance' ? ICON_R_ENTRANCE : ICON_R) * pxm) return { type: 'object', sel: { kind: 'icon', id } };
    }
  }
  const roads = Object.keys(doc.roads);
  let bestRoad: string | null = null, bestRoadD = Infinity;
  for (const id of roads) {
    const p = derived.roadPolys[id];
    if (!p || p.pts.length < 2) continue;
    const d = distPoly(p.pts, x, y, false), hw = Math.max(...p.widths) / 2;
    if (d < Math.max(hw, tol) && d < bestRoadD) { bestRoadD = d; bestRoad = id; }
  }
  if (bestRoad) return { type: 'object', sel: { kind: 'road', id: bestRoad } };
  if (only === 'road') return null;
  for (const [id, rv] of Object.entries(doc.rivers)) {
    const p = derived.riverPolys[id];
    if (p && p.length >= 2 && distPoly(p, x, y, false) < Math.max(rv.w / 2, tol)) return { type: 'object', sel: { kind: 'river', id } };
  }
  let best: string | null = null, ba = Infinity;
  for (const [id, ring] of Object.entries(derived.areaRings)) {
    if (ring.length < 3 || !pip(ring, x, y)) continue;
    const a = polyArea(ring);
    if (a < ba) { ba = a; best = id; }
  }
  return best ? { type: 'object', sel: { kind: 'area', id: best } } : null;
}

/** Index of the nearest point of `pts` to (x, y), and its distance. */
export function nearestPoint(pts: readonly Pt[], x: number, y: number): [number, number] {
  return nearestIdx(pts, x, y);
}
